import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { getProduct } from "@/shared/api/inventory";
import { getPurchaseOrder } from "@/shared/api/purchasing";
import { getSalesOrder } from "@/shared/api/sales";
import { queryKeys } from "@/shared/query/keys";
import { formatDocDateLabel } from "@/modules/shell/relative-time";
import {
  docMentionKindLabel,
  docMentionPath,
  type DocMentionKind,
} from "../mention-math";

/**
 * Mini floating doc preview for @doc mentions — plan/22.
 * Card dialog (not a full-height sidebar); View more opens the detail route.
 */
export function DocMentionPreview(props: {
  open: boolean;
  companySlug: string;
  kind: DocMentionKind | null;
  docId: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const kind = props.kind;
  const docId = props.docId;
  const enabled = props.open && Boolean(kind && docId);

  const soQuery = useQuery({
    queryKey: ["salesOrder", props.companySlug, "detail", docId ?? ""] as const,
    enabled: enabled && kind === "sales_order",
    queryFn: () => getSalesOrder(props.companySlug, docId!),
  });
  const poQuery = useQuery({
    queryKey: [
      "purchaseOrder",
      props.companySlug,
      "detail",
      docId ?? "",
    ] as const,
    enabled: enabled && kind === "purchase_order",
    queryFn: () => getPurchaseOrder(props.companySlug, docId!),
  });
  const productQuery = useQuery({
    queryKey: queryKeys.products.detail(props.companySlug, docId ?? ""),
    enabled: enabled && kind === "product",
    queryFn: () => getProduct(props.companySlug, docId!),
  });

  const pending =
    (kind === "sales_order" && soQuery.isPending) ||
    (kind === "purchase_order" && poQuery.isPending) ||
    (kind === "product" && productQuery.isPending);
  const error =
    (kind === "sales_order" && soQuery.isError && soQuery.error) ||
    (kind === "purchase_order" && poQuery.isError && poQuery.error) ||
    (kind === "product" && productQuery.isError && productQuery.error) ||
    null;

  let title = kind ? docMentionKindLabel(kind) : "Document";
  let lines: string[] = [];

  if (kind === "sales_order" && soQuery.data) {
    const o = soQuery.data.order;
    title = o.number;
    lines = [
      o.customerName,
      `Status ${o.lifecycle}`,
      o.docDate ? `Date ${formatDocDateLabel(o.docDate)}` : null,
      `Total ${o.grossTotal ?? o.total}`,
      `Paid ${o.amountPaid}`,
    ].filter(Boolean) as string[];
  } else if (kind === "purchase_order" && poQuery.data) {
    const o = poQuery.data.order;
    title = o.number;
    lines = [
      o.supplierName,
      `Status ${o.lifecycle}`,
      o.docDate ? `Date ${formatDocDateLabel(o.docDate)}` : null,
      `Total ${o.grossTotal ?? o.total}`,
      `Paid ${o.amountPaid}`,
    ].filter(Boolean) as string[];
  } else if (kind === "product" && productQuery.data) {
    const p = productQuery.data.product;
    title = p.name;
    lines = [
      p.sku ? `SKU ${p.sku}` : null,
      p.kind ? `Kind ${p.kind}` : null,
    ].filter(Boolean) as string[];
  }

  function viewMore() {
    if (!kind || !docId) return;
    props.onOpenChange(false);
    navigate(docMentionPath(props.companySlug, kind, docId));
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-sm gap-0 p-0 sm:rounded-[var(--radius-xl)]">
        <div className="border-b border-[var(--color-border)] px-4 py-3">
          <DialogTitle className="truncate text-base">{title}</DialogTitle>
          <DialogDescription className="mt-0.5 text-xs text-[var(--color-muted)]">
            {kind ? docMentionKindLabel(kind) : "Document"} · preview
          </DialogDescription>
        </div>
        <div className="space-y-2 px-4 py-4">
          {pending ? (
            <Alert variant="muted">Loading…</Alert>
          ) : error ? (
            <Alert variant="destructive">
              {error instanceof Error ? error.message : "Failed to load"}
            </Alert>
          ) : (
            <ul className="m-0 list-none space-y-1.5 p-0 text-sm text-[var(--color-foreground)]">
              {lines.map((line) => (
                <li key={line} className="text-[var(--color-muted)]">
                  <span className="text-[var(--color-foreground)]">{line}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-[var(--color-border)] px-4 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => props.onOpenChange(false)}
          >
            Close
          </Button>
          <Button type="button" size="sm" onClick={viewMore} disabled={!kind || !docId}>
            View more
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
